import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Cpu, CheckCircle2 } from 'lucide-react';
import {
  samplingLimits,
  samplingOutOfRange,
  type SamplingBound,
} from '@prakash-software/shared';
import { isApiError } from '../../lib/api';
import {
  AI_PROVIDER_LABEL,
  EVENT_VARIABLES,
  getSystemConfig,
  humanizeVar,
  isKeyOnlyAiProvider,
  listAiModels,
  putSystemConfig,
  sendTestEmail,
  testSystemAi,
  testTranscription,
  type AiModel,
  type AiProvider,
  type SystemConfig,
  type SystemConfigPatch,
} from '../../lib/webhooks-api';
import { RichHtmlEditor, type RichHtmlEditorHandle } from '../../components/RichHtmlEditor';
import { Tabs, type TabItem } from '../../components/Tabs';
import { AiCostsSection } from './AiCostsSection';
import { EmailDeliverabilitySection } from './EmailDeliverabilitySection';
import { SMTP_REASON } from '../../lib/mail-reasons';
import styles from './admin.module.css';
import { SecretInput } from './SecretInput';

const SYSTEM_TABS: TabItem[] = [
  { id: 'email', label: 'Email' },
  { id: 'template', label: 'Email template' },
  { id: 'deliverability', label: 'Email deliverability' },
  { id: 'ai', label: 'LLM / AI Setup' },
  { id: 'ai-costs', label: 'AI costs' },
];

// System configuration — the email sender (over the server's SMTP settings) +
// the one chat-end email template. Super-admin only. The cross-site delivery log lives in its own
// "Logs" tab. Variables insert (with a sentence-case label, e.g. "Name:
// {{name}}") into whichever of Subject / Body last had focus.
/**
 * Zones this browser can offer. `supportedValuesOf` is the whole IANA list;
 * the fallback covers the handful of runtimes that lack it, and the server
 * validates whatever arrives anyway.
 */
const TIME_ZONES: string[] = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['UTC', 'Asia/Kolkata', 'Asia/Dubai', 'Europe/London', 'America/New_York'];
  }
})();

/** Empty input → null ("use the provider default"), never 0. */
function numOrNull(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function intOrNull(raw: string): number | null {
  const n = numOrNull(raw);
  return n === null ? null : Math.round(n);
}

/**
 * What to show in an empty sampling box. A blank field means "omit the
 * parameter", so the value actually in force is the provider's own default —
 * printing it turns an empty control from a question into an answer. Where the
 * default belongs to a model we don't control (Ollama, a custom gateway) we
 * say so rather than invent a number.
 */
function placeholderFor(bound: SamplingBound): string {
  return bound.default === null ? "provider's default" : String(bound.default);
}

/** "Anthropic accepts 0–1, default 1." — the real API contract, not a guess. */
function rangeHint(providerLabel: string, bound: SamplingBound): string {
  const range = `${providerLabel} accepts ${bound.min}–${bound.max}`;
  return bound.default === null ? `${range}.` : `${range}, default ${bound.default}.`;
}

export function AdminSystem() {
  const [cfg, setCfg] = useState<SystemConfig | null>(null);
  const [draft, setDraft] = useState<SystemConfig | null>(null);
  const [aiApiKey, setAiApiKey] = useState(''); // write-only AI key
  const [clearAiKey, setClearAiKey] = useState(false); // user removed the stored key
  const [transcApiKey, setTranscApiKey] = useState(''); // write-only transcription key
  const [clearTranscKey, setClearTranscKey] = useState(false);
  // The key fields carry a Remove button, so they are divs with a label
  // pointing at the input, not wrapping <label>s (see the AI key field).
  const aiKeyId = useId();
  const transcKeyId = useId();
  const [aiTest, setAiTest] = useState<{ ok: boolean; msg: string } | null>(null);
  const [aiTesting, setAiTesting] = useState(false);
  // Live model list for the key-only providers. `null` = not loaded yet.
  const [models, setModels] = useState<AiModel[] | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [transcTest, setTranscTest] = useState<{ ok: boolean; msg: string } | null>(null);
  const [transcTesting, setTranscTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [tab, setTab] = useState('email');
  // Which template field a palette click should target.
  const [focusField, setFocusField] = useState<'subject' | 'body'>('body');
  const subjectRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<RichHtmlEditorHandle>(null);

  async function load() {
    try {
      const c = await getSystemConfig();
      setCfg(c);
      setDraft(c);
    } catch {
      setError('load-failed');
    }
  }
  useEffect(() => {
    void load();
  }, []);

  function set<K extends keyof SystemConfig>(key: K, value: SystemConfig[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setSaved(false);
  }

  // Monotonic token: a slow response for a provider the admin has already
  // switched away from must not overwrite the current list.
  const modelsReq = useRef(0);

  // Fetch the model list for a key-only provider. `useTypedKey` sends the
  // not-yet-saved key from the input so a fresh key can be validated before
  // saving; otherwise the server uses the stored one.
  async function loadModels(provider: AiProvider, useTypedKey = false) {
    if (!isKeyOnlyAiProvider(provider)) {
      setModels(null);
      setModelsError(null);
      return;
    }
    const req = ++modelsReq.current;
    setModelsLoading(true);
    setModelsError(null);
    try {
      const list = await listAiModels(provider, useTypedKey ? aiApiKey.trim() || null : null);
      if (req !== modelsReq.current) return; // superseded
      setModels(list);
      if (list.length === 0) setModelsError('Provider returned no chat-capable models.');
    } catch (err) {
      if (req !== modelsReq.current) return;
      setModels(null);
      const reason =
        isApiError(err) && typeof err.body === 'object' && err.body !== null
          ? (err.body as { reason?: unknown }).reason
          : null;
      setModelsError(typeof reason === 'string' ? reason : 'Could not load models.');
    } finally {
      if (req === modelsReq.current) setModelsLoading(false);
    }
  }

  // Load (and reload) the list whenever the selected provider changes. Only
  // fires once a key is on file — without one the request can only fail, and a
  // red error on first open of the tab reads as breakage rather than "set a key".
  const provider = draft?.aiProvider;
  const aiKeyOnFile = cfg?.aiApiKeySet ?? false;
  // The selected provider's real API bounds, and whether the values currently
  // in the form fit them. Checked here as well as on the server so switching
  // provider surfaces a now-invalid temperature immediately, rather than as a
  // 400 after the admin presses Save.
  const limits = useMemo(() => samplingLimits(provider ?? 'anthropic'), [provider]);
  const temperatureIssue = useMemo(
    () => samplingOutOfRange(provider ?? 'anthropic', 'temperature', draft?.aiTemperature),
    [provider, draft?.aiTemperature],
  );
  const topPIssue = useMemo(
    () => samplingOutOfRange(provider ?? 'anthropic', 'topP', draft?.aiTopP),
    [provider, draft?.aiTopP],
  );
  useEffect(() => {
    if (!provider || !isKeyOnlyAiProvider(provider)) {
      setModels(null);
      setModelsError(null);
      return;
    }
    if (!aiKeyOnFile) {
      setModels(null);
      setModelsError(null);
      return;
    }
    void loadModels(provider);
    // loadModels is stable enough for this effect's purpose (it only reads
    // aiApiKey when explicitly told to, which this call does not).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, aiKeyOnFile]);

  // Insert a labeled token into the focused field (subject input or body editor).
  function insertVar(v: string) {
    if (!draft) return;
    const token = `${humanizeVar(v)}: {{${v}}}`;
    if (focusField === 'subject') {
      const el = subjectRef.current;
      const value = draft.subjectTemplate;
      const start = el?.selectionStart ?? value.length;
      const end = el?.selectionEnd ?? start;
      set('subjectTemplate', value.slice(0, start) + token + value.slice(end));
      requestAnimationFrame(() => {
        el?.focus();
        const pos = start + token.length;
        el?.setSelectionRange(pos, pos);
      });
    } else {
      editorRef.current?.insertAtCursor(token);
    }
  }

  async function save() {
    if (!draft) return;
    // The server enforces this too, but a round trip to be told the number in
    // front of you is out of range is a worse way to learn it than the message
    // already under the field.
    const samplingIssue = temperatureIssue ?? topPIssue;
    if (samplingIssue) {
      setError(samplingIssue);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const patch: SystemConfigPatch = {
        fromName: draft.fromName,
        fromEmail: draft.fromEmail,
        emailEnabled: draft.emailEnabled,
        timezone: draft.timezone,
        subjectTemplate: draft.subjectTemplate,
        htmlTemplate: draft.htmlTemplate,
        textTemplate: draft.textTemplate,
        aiProvider: draft.aiProvider,
        aiModel: draft.aiModel,
        aiBaseUrl: draft.aiBaseUrl,
        aiTemperature: draft.aiTemperature,
        aiTopP: draft.aiTopP,
        aiMaxTokens: draft.aiMaxTokens,
        aiStopSequences: draft.aiStopSequences,
        transcriptionProvider: draft.transcriptionProvider,
        transcriptionModel: draft.transcriptionModel,
      };
      // AI key: a new value sets it, an explicit "remove" clears it, else leave.
      if (aiApiKey.trim()) patch.aiApiKey = aiApiKey.trim();
      else if (clearAiKey) patch.aiApiKey = null;
      if (transcApiKey.trim()) patch.transcriptionApiKey = transcApiKey.trim();
      else if (clearTranscKey) patch.transcriptionApiKey = null;
      const updated = await putSystemConfig(patch);
      setCfg(updated);
      setDraft(updated);
      setAiApiKey('');
      setClearAiKey(false);
      setTranscApiKey('');
      setClearTranscKey(false);
      setSaved(true);
    } catch (e) {
      // Surface the server's own explanation when it sent one — an unsafe base
      // URL or an out-of-range sampling value both say exactly what is wrong,
      // and "save-failed (400)" throws that away.
      const reason =
        isApiError(e) && typeof e.body === 'object' && e.body !== null
          ? (e.body as { reason?: unknown }).reason
          : null;
      setError(
        typeof reason === 'string'
          ? reason
          : isApiError(e)
            ? `save-failed (${e.status})`
            : 'save-failed',
      );
    } finally {
      setSaving(false);
    }
  }

  async function runTestEmail() {
    if (!testTo.trim()) return;
    setTesting(true);
    setTestMsg(null);
    try {
      const r = await sendTestEmail(testTo.trim());
      setTestMsg(
        r.ok ? `Sent to ${testTo.trim()} ✓` : `Failed: ${(r.reason && SMTP_REASON[r.reason]) ?? r.reason ?? 'unknown'}`,
      );
    } catch (e) {
      setTestMsg(
        isApiError(e) && e.body && typeof e.body === 'object' && 'reason' in e.body
          ? `Failed: ${String((e.body as { reason: unknown }).reason)}`
          : 'Failed: send error',
      );
    } finally {
      setTesting(false);
    }
  }

  // Extract a human reason from an apiFetch error (test endpoints 422 with body).
  function reasonOf(e: unknown): string {
    if (isApiError(e) && e.body && typeof e.body === 'object' && 'reason' in e.body) {
      return String((e.body as { reason: unknown }).reason);
    }
    return 'request failed';
  }

  async function runAiTest() {
    if (!draft) return;
    setAiTesting(true);
    setAiTest(null);
    try {
      // Test the currently-entered values without saving. Send the typed key
      // only when present; otherwise the server reuses the saved key.
      const r = await testSystemAi({
        provider: draft.aiProvider,
        model: draft.aiModel,
        ...(draft.aiBaseUrl ? { baseUrl: draft.aiBaseUrl } : {}),
        ...(aiApiKey.trim() ? { apiKey: aiApiKey.trim() } : {}),
      }); // 2xx only when ok
      setAiTest({ ok: true, msg: `${r.provider ?? 'LLM'} replied: “${r.text ?? ''}”` });
    } catch (e) {
      setAiTest({ ok: false, msg: reasonOf(e) });
    } finally {
      setAiTesting(false);
    }
  }

  async function runTranscTest() {
    setTranscTesting(true);
    setTranscTest(null);
    try {
      await testTranscription();
      setTranscTest({ ok: true, msg: 'Transcription key verified ✓' });
    } catch (e) {
      setTranscTest({ ok: false, msg: reasonOf(e) });
    } finally {
      setTranscTesting(false);
    }
  }

  if (error && !draft) return <p className={styles.error} role="alert">{error}</p>;
  if (!draft) return <p style={{ color: 'var(--inic-ink-muted)' }}>Loading…</p>;

  return (
    <div>
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <Cpu size={13} />
            <span>Platform Engine</span>
          </div>
          <h1 className={styles.title}>System &amp; AI Engine</h1>
          <p className={styles.subtitle}>
            Configure SendGrid email delivery, AI/LLM models, API credentials, and transcription services.
          </p>
        </div>
        {saved && (
          <div role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--inic-ok)', fontSize: 13, fontWeight: 600 }}>
            <CheckCircle2 size={16} />
            <span>Changes Saved</span>
          </div>
        )}
      </div>

      {error && <p className={styles.error} style={{ marginBottom: 16 }} role="alert">{error}</p>}

      <div className={styles.settingsLayout}>
        <Tabs
          items={SYSTEM_TABS}
          active={tab}
          onChange={setTab}
          orientation="vertical"
          ariaLabel="System configuration sections"
        />
        <div className={styles.settingsPanel}>
          {tab === 'email' && (
            <section className={styles.section}>
              <p className={styles.kicker}>SMTP</p>
              <div className={styles.form}>
                <div className={styles.field} style={{ gridColumn: '1 / -1' }} role="status">
                  <span className={styles.label}>Mail server</span>
                  {!cfg ? (
                    <span>…</span>
                  ) : cfg.smtp.configured ? (
                    <span>
                      {cfg.smtp.host}:{cfg.smtp.port} ({cfg.smtp.secure ? 'TLS' : 'STARTTLS'}) — sending as{' '}
                      {cfg.smtp.fromName ? `${cfg.smtp.fromName} <${cfg.smtp.fromEmail}>` : cfg.smtp.fromEmail}
                    </span>
                  ) : (
                    <span>
                      Not configured: {SMTP_REASON[cfg.smtp.reason] ?? cfg.smtp.reason}. No email is sent until it is.
                    </span>
                  )}
                  <span className={styles.hint}>
                    Every email — chat-end, CSAT, sign-up verification, password reset, invites — goes through this
                    server. It is set with the <code>SMTP_*</code> variables in the server&rsquo;s environment; a sender
                    below overrides <code>SMTP_FROM_EMAIL</code>.
                  </span>
                </div>
                <label className={styles.field}>
                  <span className={styles.label}>From name (optional)</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={draft.fromName ?? ''}
                    onChange={(e) => set('fromName', e.target.value || null)}
                    placeholder="Openline"
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>From email (optional)</span>
                  <input
                    className={styles.input}
                    type="email"
                    value={draft.fromEmail ?? ''}
                    onChange={(e) => set('fromEmail', e.target.value || null)}
                    placeholder="support@yoursite.com"
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Timestamp timezone</span>
                  <select
                    className={styles.select}
                    value={draft.timezone ?? ''}
                    onChange={(e) => set('timezone', e.target.value || null)}
                  >
                    <option value="">UTC (default)</option>
                    {TIME_ZONES.map((z) => (
                      <option key={z} value={z}>{z}</option>
                    ))}
                  </select>
                  <span className={styles.hint}>
                    Times in outgoing email are shown in this zone and labelled with it — e.g.{' '}
                    <code>2 Sep 2026, 15:53 GMT+5:30</code>. Pick the zone your team works in, so
                    every recipient reads the same wall clock.
                  </span>
                </label>
                <label
                  className={styles.field}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--inic-space-2)' }}
                >
                  <input
                    type="checkbox"
                    checked={draft.emailEnabled}
                    onChange={(e) => set('emailEnabled', e.target.checked)}
                  />
                  <span className={styles.label} style={{ margin: 0 }}>
                    Send chat-end email on session end
                  </span>
                </label>
                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => void save()}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save →'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {tab === 'ai' && (
            <>
              <section className={styles.section}>
                <p className={styles.kicker}>AI assistant (LLM)</p>
                <div
                  style={{
                    color: 'var(--inic-ink-muted)',
                    fontSize: 'var(--inic-size-xs)',
                    marginTop: 0,
                    lineHeight: 1.5,
                  }}
                >
                  <p style={{ margin: '0 0 var(--inic-space-1)' }}>
                    System-wide default for the visitor AI assistant. The key is stored encrypted
                    and never shown again.
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong>This is the only place AI credentials live.</strong> Every site uses
                    this provider and key; a site supplies just its own system prompt (Site → AI).
                    If no key is set here, the matching <code>*_API_KEY</code> in the server env is
                    used as a fallback — except for <code>custom</code>, which always needs its own
                    key and base URL. With no key anywhere, sites simply skip the AI reply.
                  </p>
                </div>
                <div className={styles.form}>
                  <label className={styles.field}>
                    <span className={styles.label}>Provider</span>
                    <select
                      className={styles.select}
                      value={draft.aiProvider}
                      onChange={(e) => {
                        const next = e.target.value as AiProvider;
                        set('aiProvider', next);
                        // The saved model belongs to the old provider — clear it so a
                        // stale identifier can't be saved against the new one.
                        set('aiModel', null);
                      }}
                    >
                      {(Object.keys(AI_PROVIDER_LABEL) as AiProvider[]).map((p) => (
                        <option key={p} value={p}>
                          {AI_PROVIDER_LABEL[p]}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* The KEY comes before the model, because the model list is
                      fetched with it — asking for a model first left the
                      dropdown empty and unexplained until the admin scrolled
                      past it, typed a key, and scrolled back. */}
                  {/* A div, not a <label>: a label wrapping the Remove button
                      made that button its control, so clicking "API key"
                      pressed Remove and the input had no accessible name. */}
                  <div className={styles.field}>
                    {/* Label and its action share a row rather than the action
                        being absolutely positioned over the corner — the label
                        carries the key's own last-4, so an overlay lands on top
                        of text instead of beside it. */}
                    <span className={styles.fieldHead}>
                      <label htmlFor={aiKeyId} className={styles.label}>
                        API key{' '}
                        {cfg?.aiApiKeySet && !clearAiKey ? (
                          <em style={{ fontStyle: 'normal' }}>
                            — saved (••••{cfg.aiKeyLast4 ?? '????'})
                          </em>
                        ) : (
                          <em style={{ fontStyle: 'normal' }}>— not set</em>
                        )}
                      </label>
                      {cfg?.aiApiKeySet && !clearAiKey && (
                        <button
                          type="button"
                          className={styles.buttonDanger}
                          // Short, because it shares the row with the label —
                          // which already says what it removes.
                          title="Remove the saved API key"
                          onClick={() => {
                            setClearAiKey(true);
                            setAiApiKey('');
                            // The list on screen was fetched with the key being
                            // removed; leaving it up would offer models that are
                            // about to become unreachable.
                            setModels(null);
                            setModelsError(null);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </span>
                    <SecretInput
                      id={aiKeyId}
                      value={aiApiKey}
                      onChange={(e) => {
                        setAiApiKey(e.target.value);
                        setClearAiKey(false);
                      }}
                      placeholder={
                        cfg?.aiApiKeySet && !clearAiKey
                          ? 'Leave blank to keep current key'
                          : 'Enter provider API key'
                      }
                    />
                    <span className={styles.hint}>
                      {clearAiKey
                        ? 'The saved key will be removed when you save.'
                        : 'Stored encrypted, and never shown again once saved.'}
                    </span>
                  </div>
                  {isKeyOnlyAiProvider(draft.aiProvider) ? (
                    <label className={styles.field}>
                      <span className={styles.label}>
                        Model{' '}
                        {modelsLoading ? (
                          <em style={{ fontStyle: 'normal' }}>— loading…</em>
                        ) : models ? (
                          <em style={{ fontStyle: 'normal' }}>
                            — {models.length} available
                          </em>
                        ) : null}
                      </span>
                      <select
                        className={styles.select}
                        value={draft.aiModel ?? ''}
                        disabled={modelsLoading || !models || models.length === 0}
                        onChange={(e) => set('aiModel', e.target.value || null)}
                      >
                        <option value="">
                          {models === null && !modelsLoading
                            ? 'Load models to choose one'
                            : 'Provider default'}
                        </option>
                        {/* A model saved before it left the provider's list would
                    otherwise silently reset to "default" on open. */}
                        {draft.aiModel && !models?.some((m) => m.id === draft.aiModel) && (
                          <option value={draft.aiModel}>
                            {draft.aiModel} (saved — not in list)
                          </option>
                        )}
                        {(models ?? []).map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                      <div className={styles.fieldAction}>
                        <button
                          type="button"
                          className={styles.buttonGhost}
                          // Loading with no key at all can only 404/401, and a
                          // red error on a fresh install reads as breakage
                          // rather than "there is a step above this one".
                          disabled={modelsLoading || (!aiKeyOnFile && aiApiKey.trim() === '')}
                          onClick={() => void loadModels(draft.aiProvider, true)}
                        >
                          {modelsLoading ? 'Loading…' : 'Load models'}
                        </button>
                        <span className={styles.hint}>
                          {modelsError ? (
                            <span className={styles.hintError}>{modelsError}</span>
                          ) : !aiKeyOnFile && aiApiKey.trim() === '' ? (
                            'Enter the API key above first.'
                          ) : models ? (
                            'Fetched live from the provider.'
                          ) : (
                            // The typed key is sent with this request, so a new
                            // key can be checked before it is ever stored.
                            'Loads with the key above — no need to save first.'
                          )}
                        </span>
                      </div>
                    </label>
                  ) : (
                    <label className={styles.field}>
                      <span className={styles.label}>Model (optional)</span>
                      <input
                        className={styles.input}
                        type="text"
                        value={draft.aiModel ?? ''}
                        onChange={(e) => set('aiModel', e.target.value || null)}
                        placeholder={
                          draft.aiProvider === 'openline'
                            ? 'qwen3:14b'
                            : 'model name your gateway expects'
                        }
                      />
                      <span
                        style={{
                          fontSize: 'var(--inic-size-xs)',
                          color: 'var(--inic-ink-muted)',
                          marginTop: 'var(--inic-space-1)',
                        }}
                      >
                        No model discovery for this provider — enter the identifier it expects.
                      </span>
                    </label>
                  )}
                  {/* Only 'custom' has a configurable host. The built-ins always use
              their canonical endpoint, so showing the field would imply an
              override that the orchestrator deliberately ignores. */}
                  {draft.aiProvider === 'custom' && (
                    <label className={styles.field}>
                      <span className={styles.label}>Base URL (required)</span>
                      <input
                        className={styles.input}
                        type="text"
                        value={draft.aiBaseUrl ?? ''}
                        onChange={(e) => set('aiBaseUrl', e.target.value || null)}
                        placeholder="https://your-gateway.example.com"
                      />
                      <span
                        style={{
                          fontSize: 'var(--inic-size-xs)',
                          color: 'var(--inic-ink-muted)',
                          marginTop: 'var(--inic-space-1)',
                        }}
                      >
                        Must be https and publicly resolvable. Uses the OpenAI Chat Completions wire
                        format.
                      </span>
                    </label>
                  )}
                  {/* Sampling parameters — one row, because they are read together:
              you set a temperature relative to a top-p, and a token cap
              relative to both. Every field is optional; blank omits the
              parameter entirely so the provider's own default applies, which
              is NOT the same as sending zero. These four are the ones all five
              providers accept, so no field here is a lie for some of them.

              The RANGES are the selected provider's own, not a generic 0–2:
              Anthropic 400s on anything above 1.0, so a shared control would
              have offered a value that cannot work and said nothing about it
              until a visitor got no reply. When a field is blank the
              placeholder shows the number the provider's API actually uses,
              so "empty" reads as a value rather than as nothing. */}
                  <fieldset className={styles.paramGroup}>
                    <legend className={styles.label}>Generation parameters</legend>
                    <div className={styles.paramRow}>
                      <label className={styles.field}>
                        <span className={styles.label}>Temperature</span>
                        <input
                          className={styles.input}
                          type="number"
                          min={limits.temperature.min}
                          max={limits.temperature.max}
                          step={limits.temperature.step}
                          value={draft.aiTemperature ?? ''}
                          onChange={(e) => set('aiTemperature', numOrNull(e.target.value))}
                          placeholder={placeholderFor(limits.temperature)}
                        />
                        <span className={styles.hint}>
                          {rangeHint(AI_PROVIDER_LABEL[draft.aiProvider], limits.temperature)}{' '}
                          Higher is more random.
                        </span>
                        {temperatureIssue && (
                          <span className={styles.hintError}>{temperatureIssue}</span>
                        )}
                      </label>
                      <label className={styles.field}>
                        <span className={styles.label}>Top P</span>
                        <input
                          className={styles.input}
                          type="number"
                          min={limits.topP.min}
                          max={limits.topP.max}
                          step={limits.topP.step}
                          value={draft.aiTopP ?? ''}
                          onChange={(e) => set('aiTopP', numOrNull(e.target.value))}
                          placeholder={placeholderFor(limits.topP)}
                        />
                        <span className={styles.hint}>
                          {rangeHint(AI_PROVIDER_LABEL[draft.aiProvider], limits.topP)} Tune this or
                          temperature, rarely both.
                        </span>
                        {topPIssue && <span className={styles.hintError}>{topPIssue}</span>}
                      </label>
                      <label className={styles.field}>
                        <span className={styles.label}>Max tokens</span>
                        <input
                          className={styles.input}
                          type="number"
                          min={1}
                          max={200000}
                          step={1}
                          value={draft.aiMaxTokens ?? ''}
                          onChange={(e) => set('aiMaxTokens', intOrNull(e.target.value))}
                          placeholder="4096"
                        />
                        <span className={styles.hint}>Output cap per reply.</span>
                      </label>
                      <label className={styles.field}>
                        <span className={styles.label}>Stop sequences</span>
                        <input
                          className={styles.input}
                          type="text"
                          value={draft.aiStopSequences.join(', ')}
                          onChange={(e) =>
                            set(
                              'aiStopSequences',
                              e.target.value
                                .split(',')
                                .map((x) => x.trim())
                                .filter(Boolean)
                                .slice(0, 4),
                            )
                          }
                          placeholder="END, ###"
                        />
                        <span className={styles.hint}>Comma-separated, up to 4.</span>
                      </label>
                    </div>
                    <p className={styles.paramNote}>
                      A blank field is omitted from the request, so the provider&rsquo;s own default
                      applies — shown in grey inside the box where we can state it. That is not the
                      same as entering <code>0</code>, which is a real, and very different,
                      temperature. Reasoning models spend the token budget on thinking before any
                      visible text — below ~1500 they can return an empty reply.
                    </p>
                  </fieldset>
                  <div
                    className={styles.formActions}
                    style={{ alignItems: 'center', gap: 'var(--inic-space-2)' }}
                  >
                    <button
                      type="button"
                      className={styles.button}
                      onClick={() => void save()}
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save →'}
                    </button>
                    <button
                      type="button"
                      className={styles.buttonGhost}
                      onClick={() => void runAiTest()}
                      disabled={aiTesting}
                    >
                      {aiTesting ? 'Testing…' : 'Test connection'}
                    </button>
                    {aiTest && (
                      <span
                        style={{
                          fontSize: 'var(--inic-size-xs)',
                          color: aiTest.ok
                            ? 'var(--inic-ok, #15803d)'
                            : 'var(--inic-danger, #b5331d)',
                        }}
                      >
                        {aiTest.ok ? '✓ ' : '✗ '}
                        {aiTest.msg}
                      </span>
                    )}
                  </div>
                  <p
                    style={{
                      gridColumn: '1 / -1',
                      color: 'var(--inic-ink-muted)',
                      fontSize: 'var(--inic-size-xs)',
                      margin: 0,
                    }}
                  >
                    The test uses the <em>saved</em> config — save your changes first.
                  </p>
                </div>
              </section>

              <section className={styles.section}>
                <p className={styles.kicker}>Transcription (speech-to-text)</p>
                <p
                  style={{
                    color: 'var(--inic-ink-muted)',
                    fontSize: 'var(--inic-size-xs)',
                    marginTop: 0,
                  }}
                >
                  Used to transcribe call recordings. Key stored encrypted; falls back to{' '}
                  <code>GROQ_API_KEY</code> / <code>GROQ_TRANSCRIBE_MODEL</code> in the server env
                  when unset.
                </p>
                <div className={styles.form}>
                  <label className={styles.field}>
                    <span className={styles.label}>Provider</span>
                    <select
                      className={styles.select}
                      value={draft.transcriptionProvider}
                      onChange={(e) =>
                        set(
                          'transcriptionProvider',
                          e.target.value as SystemConfig['transcriptionProvider'],
                        )
                      }
                    >
                      <option value="groq">groq</option>
                    </select>
                  </label>
                  <label className={styles.field}>
                    <span className={styles.label}>Model (optional)</span>
                    <input
                      className={styles.input}
                      type="text"
                      value={draft.transcriptionModel ?? ''}
                      onChange={(e) => set('transcriptionModel', e.target.value || null)}
                      placeholder="whisper-large-v3-turbo"
                    />
                  </label>
                  <div className={styles.field}>
                    <label htmlFor={transcKeyId} className={styles.label}>
                      API key{' '}
                      {cfg?.transcriptionKeySet && !clearTranscKey ? (
                        <em style={{ fontStyle: 'normal' }}>
                          — saved (••••{cfg.transcriptionKeyLast4 ?? '????'})
                        </em>
                      ) : (
                        <em style={{ fontStyle: 'normal' }}>— not set</em>
                      )}
                    </label>
                    <SecretInput
                      id={transcKeyId}
                      value={transcApiKey}
                      onChange={(e) => {
                        setTranscApiKey(e.target.value);
                        setClearTranscKey(false);
                      }}
                      placeholder={
                        cfg?.transcriptionKeySet && !clearTranscKey
                          ? 'Leave blank to keep current key'
                          : 'gsk_…'
                      }
                    />
                    {cfg?.transcriptionKeySet && !clearTranscKey && (
                      <button
                        type="button"
                        className={styles.buttonGhost}
                        style={{ alignSelf: 'flex-start', marginTop: 'var(--inic-space-1)' }}
                        onClick={() => {
                          setClearTranscKey(true);
                          setTranscApiKey('');
                        }}
                      >
                        Remove saved key
                      </button>
                    )}
                  </div>
                  <div
                    className={styles.formActions}
                    style={{ alignItems: 'center', gap: 'var(--inic-space-2)' }}
                  >
                    <button
                      type="button"
                      className={styles.button}
                      onClick={() => void save()}
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save →'}
                    </button>
                    <button
                      type="button"
                      className={styles.buttonGhost}
                      onClick={() => void runTranscTest()}
                      disabled={transcTesting}
                    >
                      {transcTesting ? 'Testing…' : 'Test connection'}
                    </button>
                    {transcTest && (
                      <span
                        style={{
                          fontSize: 'var(--inic-size-xs)',
                          color: transcTest.ok
                            ? 'var(--inic-ok, #15803d)'
                            : 'var(--inic-danger, #b5331d)',
                        }}
                      >
                        {transcTest.ok ? '✓ ' : '✗ '}
                        {transcTest.msg}
                      </span>
                    )}
                  </div>
                </div>
              </section>
            </>
          )}

          {tab === 'ai-costs' && <AiCostsSection />}

          {tab === 'deliverability' && <EmailDeliverabilitySection />}

          {tab === 'template' && (
            <section className={styles.section}>
              <p className={styles.kicker}>Chat-end email template</p>
              <div className={styles.form}>
                <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                  <span className={styles.label}>Subject</span>
                  <input
                    ref={subjectRef}
                    className={styles.input}
                    type="text"
                    value={draft.subjectTemplate}
                    onFocus={() => setFocusField('subject')}
                    onChange={(e) => set('subjectTemplate', e.target.value)}
                  />
                </label>

                <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                  <span className={styles.label}>
                    Insert a variable — click to drop it into the{' '}
                    {focusField === 'subject' ? 'subject' : 'body'} at the cursor
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--inic-space-1)' }}>
                    {EVENT_VARIABLES['session.ended'].map((v) => (
                      <button
                        key={v}
                        type="button"
                        className={styles.badgeAccent}
                        style={{ cursor: 'pointer', border: 'none' }}
                        // preventDefault keeps focus/selection in the target field.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertVar(v)}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                  <span className={styles.label}>HTML body</span>
                  <RichHtmlEditor
                    ref={editorRef}
                    value={draft.htmlTemplate}
                    onChange={(html) => set('htmlTemplate', html)}
                    onFocus={() => setFocusField('body')}
                    placeholder="Hi {{name}}, thanks for chatting with us…"
                  />
                </div>

                <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                  <span className={styles.label}>Plain-text body (fallback)</span>
                  <textarea
                    className={styles.input}
                    rows={5}
                    value={draft.textTemplate}
                    onChange={(e) => set('textTemplate', e.target.value)}
                    style={{
                      resize: 'vertical',
                      fontFamily: 'var(--inic-font-mono)',
                      fontSize: 'var(--inic-size-xs)',
                      lineHeight: 1.45,
                    }}
                  />
                </label>
                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => void save()}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save →'}
                  </button>
                </div>
                <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                  <span className={styles.label}>
                    Send a test (uses sample session data — save your changes first)
                  </span>
                  <span
                    style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'center' }}
                  >
                    <input
                      className={styles.input}
                      type="email"
                      value={testTo}
                      placeholder="you@example.com"
                      onChange={(e) => setTestTo(e.target.value)}
                      style={{ maxWidth: 280 }}
                    />
                    <button
                      type="button"
                      className={styles.buttonGhost}
                      onClick={() => void runTestEmail()}
                      disabled={testing || !testTo.trim()}
                    >
                      {testing ? 'Sending…' : 'Send test'}
                    </button>
                    {testMsg && (
                      <span
                        style={{ fontSize: 'var(--inic-size-xs)', color: 'var(--inic-ink-muted)' }}
                      >
                        {testMsg}
                      </span>
                    )}
                  </span>
                </div>
                <p
                  style={{
                    gridColumn: '1 / -1',
                    color: 'var(--inic-ink-muted)',
                    fontSize: 'var(--inic-size-xs)',
                    margin: 0,
                  }}
                >
                  Recipients are resolved per session: the site’s chat-end address + the agents who
                  handled it, cc’ing the visitor when their email is known.
                </p>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
